import { useState, useEffect, useCallback } from 'react';
import { COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI } from '../config.js';

// In-memory token storage with sessionStorage persistence
let _idToken = sessionStorage.getItem('id_token');
let _accessToken = sessionStorage.getItem('access_token');

function base64URLEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

async function generateCodeChallenge(verifier) {
  const encoded = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return base64URLEncode(digest);
}

function decodeJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

export async function login() {
  const verifier = await generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  sessionStorage.setItem('pkce_verifier', verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: COGNITO_CLIENT_ID,
    redirect_uri: COGNITO_REDIRECT_URI,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: challenge
  });

  window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
}

export async function handleCallback(code) {
  const verifier = sessionStorage.getItem('pkce_verifier');
  if (!verifier) throw new Error('Missing PKCE verifier');

  const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_CLIENT_ID,
      redirect_uri: COGNITO_REDIRECT_URI,
      code,
      code_verifier: verifier
    })
  });

  if (!res.ok) throw new Error(`Token exchange failed: ${res.status}`);
  const tokens = await res.json();

  _idToken = tokens.id_token;
  _accessToken = tokens.access_token;
  sessionStorage.setItem('id_token', tokens.id_token);
  sessionStorage.setItem('access_token', tokens.access_token);
  sessionStorage.removeItem('pkce_verifier');
  // Store refresh token in sessionStorage (best we can do in a pure SPA)
  if (tokens.refresh_token) {
    sessionStorage.setItem('refresh_token', tokens.refresh_token);
  }
}

export function getIdToken() {
  return _idToken;
}

export function isAuthenticated() {
  if (!_idToken) return false;
  const payload = decodeJwtPayload(_idToken);
  if (!payload) return false;
  return payload.exp * 1000 > Date.now();
}

export function logout() {
  _idToken = null;
  _accessToken = null;
  sessionStorage.removeItem('id_token');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');

  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    logout_uri: window.location.origin
  });
  window.location.href = `https://${COGNITO_DOMAIN}/logout?${params}`;
}

function getUserFromToken() {
  if (!_idToken) return null;
  const payload = decodeJwtPayload(_idToken);
  if (!payload) return null;
  return { email: payload.email, name: payload.name || payload.email, sub: payload.sub };
}

export function useAuth() {
  const [user, setUser] = useState(() => getUserFromToken());
  const [authed, setAuthed] = useState(() => isAuthenticated());

  useEffect(() => {
    const interval = setInterval(() => {
      const ok = isAuthenticated();
      setAuthed(ok);
      setUser(ok ? getUserFromToken() : null);
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const doLogin = useCallback(() => login(), []);
  const doLogout = useCallback(() => logout(), []);

  return { user, isAuthenticated: authed, login: doLogin, logout: doLogout };
}
